'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useSession } from 'next-auth/react'
import { UsersIcon, LockIcon, SearchIcon, PlusIcon, UserIcon, TargetIcon, BoltIcon } from '@/components/Icons'

interface Team {
  id: string
  name: string
  description?: string
  avatar?: string
  isPublic: boolean
  maxMembers: number
  memberCount: number
  createdAt: string
  creator: {
    id: string
    name: string
    username: string
  }
  isOwner: boolean
  isMember: boolean
  role?: string
}

export default function TeamsPage() {
  const { data: session } = useSession()
  const [teams, setTeams] = useState<Team[]>([])
  const [publicTeams, setPublicTeams] = useState<Team[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [activeTab, setActiveTab] = useState<'my-teams' | 'discover' | 'invites'>('my-teams')
  const [isLoading, setIsLoading] = useState(true)
  const [inviteCount, setInviteCount] = useState(0)
  const [invites, setInvites] = useState<any[]>([])
  const [isProcessingInvite, setIsProcessingInvite] = useState<string | null>(null)

  useEffect(() => {
    if (session) {
      fetchTeams()
      fetchInviteCount()
    }
  }, [session])

  useEffect(() => {
    if (activeTab === 'discover') {
      fetchPublicTeams()
    } else if (activeTab === 'invites') {
      fetchInvites()
    }
  }, [activeTab, searchTerm])

  const fetchTeams = async () => {
    try {
      const response = await fetch('/api/teams')
      const data = await response.json()
      if (response.ok) {
        setTeams(data)
      }
    } catch (error) {
      console.error('Error fetching teams:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchPublicTeams = async () => {
    try {
      const url = searchTerm 
        ? `/api/teams/discover?search=${encodeURIComponent(searchTerm)}`
        : '/api/teams/discover'
      const response = await fetch(url)
      const data = await response.json()
      if (response.ok) {
        setPublicTeams(data)
      }
    } catch (error) {
      console.error('Error fetching public teams:', error)
    }
  }

  const fetchInviteCount = async () => {
    try {
      const response = await fetch('/api/teams/invites')
      const data = await response.json()
      if (response.ok) {
        setInviteCount(data.length)
      }
    } catch (error) {
      console.error('Error fetching invites:', error)
    }
  }

  const fetchInvites = async () => {
    try {
      const response = await fetch('/api/teams/invites')
      const data = await response.json()
      if (response.ok) {
        setInvites(data)
        setInviteCount(data.length)
      }
    } catch (error) {
      console.error('Error fetching invites:', error)
    }
  }

  const joinTeam = async (teamId: string) => {
    try {
      const response = await fetch(`/api/teams/${teamId}/join`, {
        method: 'POST',
      })
      if (response.ok) {
        fetchPublicTeams()
        fetchTeams()
      }
    } catch (error) {
      console.error('Error joining team:', error)
    }
  }

  const respondToInvite = async (inviteId: string, action: 'accept' | 'decline') => {
    setIsProcessingInvite(inviteId)
    try {
      const response = await fetch(`/api/teams/invites/${inviteId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action })
      })

      const data = await response.json()

      if (response.ok) {
        // Refresh invites and teams
        fetchInvites()
        fetchTeams()
        
        // Show success message
        if (action === 'accept') {
          alert(`Successfully joined ${data.teamName || 'the team'}!`)
        }
      } else {
        alert(data.error || `Failed to ${action} invite`)
      }
    } catch (error) {
      alert('Network error occurred')
    } finally {
      setIsProcessingInvite(null)
    }
  }

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'creator':
        return <span className="inline-block w-4 h-4 text-center">👑</span>
      case 'admin':
        return <span className="inline-block w-4 h-4 text-center">👑</span>
      default:
        return <span className="inline-block w-4 h-4 text-center"><UsersIcon className="w-4 h-4" /></span>
    }
  }

  const getTeamTypeIcon = (isPublic: boolean) => {
    return isPublic 
      ? <span className="inline-block w-4 h-4 text-center">🌍</span>
      : <span className="inline-block w-4 h-4 text-center"><LockIcon className="w-4 h-4" /></span>
  }

  if (!session) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-16">
          <span className="text-xl"><UsersIcon className="w-4 h-4" /></span>
          <h2 className="text-2xl font-bold text-white mb-4">Join MathLeet Teams</h2>
          <p className="text-gray-200 mb-8">Sign in to create or join teams and collaborate on math problems</p>
          <Link href="/auth/signin" className="btn btn-primary">
            Sign In
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold glow-text mb-2">Math Teams</h1>
            <p className="text-gray-200">
              Collaborate with other mathematicians, share problems, and solve together
            </p>
          </div>
          <Link href="/teams/create" className="btn btn-primary flex items-center gap-2">
            <span className="inline-block w-5 h-5 text-center text-lg">➕</span>
            Create Team
          </Link>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setActiveTab('my-teams')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'my-teams'
                ? 'btn-primary'
                : 'btn-secondary'
            }`}
          >
            <span className="inline-block w-4 h-4 text-center mr-2"><UsersIcon className="w-4 h-4" /></span>
            My Teams ({teams.length})
          </button>
          <button
            onClick={() => setActiveTab('discover')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'discover'
                ? 'btn-primary'
                : 'btn-secondary'
            }`}
          >
            <span className="inline-block w-4 h-4 text-center mr-2"><SearchIcon className="w-4 h-4" /></span>
            Discover
          </button>
          <button
            onClick={() => setActiveTab('invites')}
            className={`px-4 py-2 rounded-lg font-medium transition-all relative ${
              activeTab === 'invites'
                ? 'btn-primary'
                : 'btn-secondary'
            }`}
          >
            <span className="inline-block w-4 h-4 text-center mr-2"><PlusIcon className="w-4 h-4" /></span>
            Invites
            {inviteCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {inviteCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* My Teams Tab */}
      {activeTab === 'my-teams' && (
        <div>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="card-energy p-6 animate-pulse">
                  <div className="flex items-center space-x-4">
                    <div className="bg-gray-600 h-12 w-12 rounded-lg"></div>
                    <div className="flex-1">
                      <div className="bg-gray-600 h-4 w-48 rounded mb-2"></div>
                      <div className="bg-gray-600 h-3 w-32 rounded"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : teams.length === 0 ? (
            <div className="text-center py-12 card-energy">
              <span className="text-xl"><UsersIcon className="w-4 h-4" /></span>
              <h3 className="text-xl font-semibold text-white mb-2">No Teams Yet</h3>
              <p className="text-gray-300 mb-6">Create your first team or discover public teams to join</p>
              <div className="flex gap-4 justify-center">
                <Link href="/teams/create" className="btn btn-primary">
                  Create Team
                </Link>
                <button
                  onClick={() => setActiveTab('discover')}
                  className="btn btn-secondary"
                >
                  Discover Teams
                </button>
              </div>
            </div>
          ) : (
            <div className="grid gap-4">
              {teams.map((team) => (
                <div key={team.id} className="card-energy p-6 interactive-hover super-glow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="relative">
                        <div className="h-12 w-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                          <span className="text-white font-bold text-lg">
                            {team.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="absolute -bottom-1 -right-1">
                          {getRoleIcon(team.role || 'member')}
                        </div>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-semibold glow-text-secondary">{team.name}</h3>
                          {getTeamTypeIcon(team.isPublic)}
                        </div>
                        <p className="text-sm text-gray-300 mb-2">
                          {team.description || 'No description'}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <span className="inline-block w-3 h-3 text-center text-xs"><UsersIcon className="w-4 h-4" /></span>
                            {team.memberCount}/{team.maxMembers} members
                          </span>
                          <span>Created by {team.creator.name}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Link
                        href={`/teams/${team.id}`}
                        className="btn btn-primary flex items-center gap-2"
                      >
                        <span className="inline-block w-4 h-4 text-center">💬</span>
                        Open
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Discover Tab */}
      {activeTab === 'discover' && (
        <div>
          <div className="mb-6">
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 inline-block w-4 h-4 text-center"><SearchIcon className="w-4 h-4" /></span>
              <input
                type="text"
                placeholder="Search public teams..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          <div className="grid gap-4">
            {publicTeams.map((team) => (
              <div key={team.id} className="card-energy p-6 interactive-hover super-glow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-lg">
                        {team.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold glow-text-secondary">{team.name}</h3>
                        <span className="inline-block w-4 h-4 text-center">🌍</span>
                      </div>
                      <p className="text-sm text-gray-300 mb-2">
                        {team.description || 'No description'}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <span className="inline-block w-3 h-3 text-center text-xs"><UsersIcon className="w-4 h-4" /></span>
                          {team.memberCount}/{team.maxMembers} members
                        </span>
                        <span>By {team.creator.name}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {team.isMember ? (
                      <Link
                        href={`/teams/${team.id}`}
                        className="btn btn-primary flex items-center gap-2"
                      >
                        <span className="inline-block w-4 h-4 text-center">💬</span>
                        Open
                      </Link>
                    ) : (
                      <button
                        onClick={() => joinTeam(team.id)}
                        className="btn btn-secondary flex items-center gap-2"
                        disabled={team.memberCount >= team.maxMembers}
                      >
                        <span className="inline-block w-4 h-4 text-center"><PlusIcon className="w-4 h-4" /></span>
                        {team.memberCount >= team.maxMembers ? 'Full' : 'Join'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {publicTeams.length === 0 && (
            <div className="text-center py-12 card-energy">
              <span className="text-xl"><SearchIcon className="w-4 h-4" /></span>
              <h3 className="text-xl font-semibold text-white mb-2">No Public Teams Found</h3>
              <p className="text-gray-300">Try adjusting your search or create your own team</p>
            </div>
          )}
        </div>
      )}

      {/* Invites Tab */}
      {activeTab === 'invites' && (
        <div>
          {invites.length === 0 ? (
            <div className="text-center py-12 card-energy">
              <span className="text-xl"><PlusIcon className="w-4 h-4" /></span>
              <h3 className="text-xl font-semibold text-white mb-2">No Invitations</h3>
              <p className="text-gray-300">Team invitations will appear here when you receive them</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {invites.map((invite) => (
                <div key={invite.id} className="card-energy p-6 interactive-hover">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="h-12 w-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-lg">
                          {invite.team.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white glow-text-secondary">
                          {invite.team.name}
                        </h3>
                        <p className="text-gray-300">
                          Invited by {invite.sender.name || invite.sender.username}
                        </p>
                        {invite.team.description && (
                          <p className="text-gray-400 text-sm mt-1">
                            {invite.team.description}
                          </p>
                        )}
                        {invite.message && (
                          <p className="text-blue-400 text-sm mt-2 italic">
                            &quot;{invite.message}&quot;
                          </p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-gray-400 mt-2">
                          <div className="flex items-center">
                            <span className="inline-block w-3 h-3 text-center text-xs mr-1"><UsersIcon className="w-4 h-4" /></span>
                            {invite.team._count.members} members
                          </div>
                          {invite.team.isPublic ? (
                            <div className="flex items-center">
                              <span className="inline-block w-3 h-3 text-center text-xs mr-1">🌍</span>
                              Public
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <span className="inline-block w-3 h-3 text-center text-xs mr-1"><LockIcon className="w-4 h-4" /></span>
                              Private
                            </div>
                          )}
                          <div>
                            Expires: {new Date(invite.expiresAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => respondToInvite(invite.id, 'decline')}
                        disabled={isProcessingInvite === invite.id}
                        className="btn btn-secondary text-sm py-2 px-4 disabled:opacity-50"
                      >
                        Decline
                      </button>
                      <button
                        onClick={() => respondToInvite(invite.id, 'accept')}
                        disabled={isProcessingInvite === invite.id || invite.team._count.members >= invite.team.maxMembers}
                        className="btn btn-primary text-sm py-2 px-4 disabled:opacity-50 flex items-center gap-2"
                      >
                        {isProcessingInvite === invite.id ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                            Processing...
                          </>
                        ) : invite.team._count.members >= invite.team.maxMembers ? (
                          'Team Full'
                        ) : (
                          <>
                            <span className="inline-block w-4 h-4 text-center"><PlusIcon className="w-4 h-4" /></span>
                            Accept
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Feature Showcase */}
      <div className="mt-12 grid md:grid-cols-3 gap-6">
        <div className="card-energy p-6 text-center interactive-hover">
          <span className="text-xl">💬</span>
          <h3 className="text-lg font-semibold mb-2 glow-text-secondary">Real-time Chat</h3>
          <p className="text-gray-200 text-sm">
            Discuss problems and solutions with your team members in real-time
          </p>
        </div>
        
        <div className="card-energy p-6 text-center interactive-hover">
          <span className="text-xl"><TargetIcon className="h-5 w-5" /></span>
          <h3 className="text-lg font-semibold mb-2 glow-text-secondary">Problem Sharing</h3>
          <p className="text-gray-200 text-sm">
            Share interesting problems with your team and work together to solve them
          </p>
        </div>
        
        <div className="card-energy p-6 text-center interactive-hover">
          <span className="text-xl"><BoltIcon className="h-5 w-5" /></span>
          <h3 className="text-lg font-semibold mb-2 glow-text-secondary">Collaborative Learning</h3>
          <p className="text-gray-200 text-sm">
            Learn from each other&apos;s approaches and improve your problem-solving skills
          </p>
        </div>
      </div>
    </div>
  )
}